import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { classLabel } from './contexts';
import { type Db, must, useDb } from './db';

/**
 * Boards — the online whiteboard of a course (docs/plan.md §3, «Дошка»). The scene is Excalidraw's
 * (the web app knows its shape; here it is plain JSON), pictures are files in the private bucket
 * `boards` next to it, saved under their Excalidraw file id.
 */
export interface BoardScene {
  elements: unknown[];
  /** pictures on the board: file id → its type; the bytes live in the bucket */
  files?: Record<string, { mimeType: string }>;
  /** the board's own settings and what the class added on top of the drawing */
  extra?: Record<string, unknown>;
}

export interface BoardSummary {
  id: string;
  schoolId: string;
  courseId: string;
  lessonId: string | null;
  title: string;
  shared: boolean;
  version: number;
  updatedAt: string;
  subject: string;
  classLabel: string;
}

export interface Board extends BoardSummary {
  scene: BoardScene;
}

const SUMMARY =
  'id, school_id, course_id, lesson_id, title, shared, version, updated_at, courses (group_label, subjects (name), classes (grade, letter))';

interface Row {
  id: string;
  school_id: string;
  course_id: string;
  lesson_id: string | null;
  title: string;
  shared: boolean;
  version: number;
  updated_at: string;
  courses: {
    group_label: string | null;
    subjects: { name: string } | null;
    classes: { grade: number; letter: string } | null;
  } | null;
}

const summary = (r: Row): BoardSummary => ({
  id: r.id,
  schoolId: r.school_id,
  courseId: r.course_id,
  lessonId: r.lesson_id,
  title: r.title,
  shared: r.shared,
  version: r.version,
  updatedAt: r.updated_at,
  subject:
    (r.courses?.subjects?.name ?? '') +
    (r.courses?.group_label ? ` (${r.courses.group_label})` : ''),
  classLabel: r.courses?.classes ? classLabel(r.courses.classes) : '',
});

export const EMPTY_SCENE: BoardScene = { elements: [] };

/** Boards of a course, or every board the person may see in a school; the newest first. */
export function useBoards(filter: { courseId: string } | { schoolId: string }) {
  const db = useDb();
  return useQuery({
    queryKey: ['boards', filter],
    enabled: 'courseId' in filter ? filter.courseId !== '' : filter.schoolId !== '',
    queryFn: async () => {
      let query = db.from('boards').select(SUMMARY).order('updated_at', { ascending: false });
      query =
        'courseId' in filter
          ? query.eq('course_id', filter.courseId)
          : query.eq('school_id', filter.schoolId);
      return must(await query).map((r) => summary(r as Row));
    },
  });
}

/** One board without its scene (the phone app shows its picture). */
export function useBoardSummary(id: string) {
  const db = useDb();
  return useQuery({
    queryKey: ['board-summary', id],
    enabled: id !== '',
    queryFn: async () => {
      const rows = must(await db.from('boards').select(SUMMARY).eq('id', id));
      const row = rows[0] as unknown as Row | undefined;
      return row ? summary(row) : null;
    },
  });
}

export async function loadBoard(db: Db, id: string): Promise<Board | null> {
  const rows = must(await db.from('boards').select(`${SUMMARY}, scene`).eq('id', id));
  const row = rows[0] as unknown as (Row & { scene: BoardScene }) | undefined;
  return row ? { ...summary(row), scene: row.scene } : null;
}

/** One board with its scene (kept for the offline copy like any other screen). */
export function useBoard(id: string) {
  const db = useDb();
  return useQuery({
    queryKey: ['board', id],
    enabled: id !== '',
    queryFn: () => loadBoard(db, id),
  });
}

/** «Ім'я Прізвище» of a person of the school (the author of comments on a board). */
export function useMemberName(memberId: string) {
  const db = useDb();
  return useQuery({
    queryKey: ['member-name', memberId],
    enabled: memberId !== '',
    queryFn: async () => {
      const rows = must(
        await db.from('school_members').select('first_name, last_name').eq('id', memberId),
      );
      const row = rows[0];
      return row ? `${row.first_name} ${row.last_name}` : '';
    },
  });
}

/** The course of a lesson: a board in a lesson belongs to the lesson's course. */
export function useLessonCourse(lessonId: string | undefined) {
  const db = useDb();
  return useQuery({
    queryKey: ['lesson-course', lessonId],
    enabled: Boolean(lessonId),
    queryFn: async () => {
      const rows = must(
        await db
          .from('lessons')
          .select(
            `school_id, course_id, topic,
            course:courses!lessons_school_id_course_id_fkey(
              subject:subjects!courses_school_id_subject_id_fkey(name),
              class:classes!courses_school_id_class_id_fkey(grade)
            )`,
          )
          .eq('id', lessonId as string),
      );
      const row = rows[0];
      return row
        ? {
            schoolId: row.school_id,
            courseId: row.course_id,
            // for the lab: what fits this lesson
            topic: row.topic,
            subject: row.course?.subject?.name ?? '',
            grade: row.course?.class?.grade ?? null,
          }
        : null;
    },
  });
}

/**
 * Saves the scene over the version it was loaded from. Returns the new version, or null when
 * somebody saved in between (the caller loads, merges and saves again).
 */
export async function saveBoardScene(
  db: Db,
  id: string,
  version: number,
  scene: BoardScene,
): Promise<number | null> {
  const rows = must(
    await db
      .from('boards')
      .update({ scene: scene as never })
      .eq('id', id)
      .eq('version', version)
      .select('version'),
  );
  return rows[0]?.version ?? null;
}

const folder = (b: Pick<BoardSummary, 'schoolId' | 'courseId' | 'id'>) =>
  `${b.schoolId}/${b.courseId}/${b.id}/`;

/** A picture of the board into its folder (once: a file id is the hash of its bytes). */
export async function uploadBoardFile(
  db: Db,
  board: Pick<BoardSummary, 'schoolId' | 'courseId' | 'id'>,
  fileId: string,
  blob: Blob,
) {
  const { error } = await db.storage
    .from('boards')
    .upload(`${folder(board)}${fileId}`, blob, { contentType: blob.type, upsert: false });
  // already there: the same bytes
  if (error && !/exists|duplicate/i.test(error.message)) throw error;
}

export async function downloadBoardFile(
  db: Db,
  board: Pick<BoardSummary, 'schoolId' | 'courseId' | 'id'>,
  fileId: string,
): Promise<Blob> {
  const { data, error } = await db.storage.from('boards').download(`${folder(board)}${fileId}`);
  if (error) throw error;
  return data;
}

/** The picture of the board the teacher's app keeps for the phone app (written over each time). */
export async function uploadBoardPreview(
  db: Db,
  board: Pick<BoardSummary, 'schoolId' | 'courseId' | 'id'>,
  png: Blob,
) {
  const { error } = await db.storage
    .from('boards')
    .upload(`${folder(board)}preview.png`, png, { contentType: 'image/png', upsert: true });
  if (error) throw error;
}

/** A link to the board's picture for an hour; null while there is none yet. */
export async function boardPreviewLink(
  db: Db,
  board: Pick<BoardSummary, 'schoolId' | 'courseId' | 'id'>,
): Promise<string | null> {
  const { data, error } = await db.storage
    .from('boards')
    .createSignedUrl(`${folder(board)}preview.png`, 3600);
  return error ? null : data.signedUrl;
}

export function useBoardActions(schoolId: string, memberId: string) {
  const db = useDb();
  const client = useQueryClient();
  const invalidate = () => client.invalidateQueries({ queryKey: ['boards'] });
  return {
    create: useMutation({
      mutationFn: async (a: {
        courseId: string;
        title: string;
        lessonId?: string | null;
        scene?: BoardScene;
      }) => {
        const rows = must(
          await db
            .from('boards')
            .insert({
              school_id: schoolId,
              course_id: a.courseId,
              lesson_id: a.lessonId ?? null,
              title: a.title.trim(),
              scene: (a.scene ?? EMPTY_SCENE) as never,
              created_by_member_id: memberId,
            })
            .select('id'),
        );
        return (rows[0] as { id: string }).id;
      },
      onSettled: invalidate,
    }),
    update: useMutation({
      mutationFn: async (a: { id: string; title?: string; shared?: boolean }) =>
        must(
          await db
            .from('boards')
            .update({
              ...(a.title !== undefined && { title: a.title.trim() }),
              ...(a.shared !== undefined && { shared: a.shared }),
            })
            .eq('id', a.id),
        ),
      onSettled: (_data, _error, a) => {
        void invalidate();
        void client.invalidateQueries({ queryKey: ['board', a.id] });
      },
    }),
    remove: useMutation({
      mutationFn: async (b: Pick<BoardSummary, 'schoolId' | 'courseId' | 'id'>) => {
        // the pictures first: their permission needs the row
        const { data: files } = await db.storage.from('boards').list(folder(b).slice(0, -1));
        if (files?.length) {
          const { error } = await db.storage
            .from('boards')
            .remove(files.map((f) => `${folder(b)}${f.name}`));
          if (error) throw error;
        }
        must(await db.from('boards').delete().eq('id', b.id));
      },
      onSettled: invalidate,
    }),
  };
}
